import { pinGuestIdentity, pinnedGuestIdentity, randomGuestIdentity } from './guestIdentity';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
    discordRequired: boolean;
    canAttemptGuestLogin: boolean;
    authError: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isUnauthorized: boolean;
    role: string | null;
    clientId: number | null;
    accessToken: string | null;
    authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
    startDiscordLogin: () => Promise<void>;
    doGuestLogin: () => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [discordRequired, setDiscordRequired] = useState(true);
    const [canAttemptGuestLogin, setCanAttemptGuestLogin] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [clientId, setClientId] = useState<number | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    // On mount: capture session_token from URL or localStorage, then verify with /api/me using Bearer auth
    useEffect(() => {
        (async () => {
            try {
                const config = await fetch('/api/auth_config');
                if (!config.ok) throw new Error('Could not load sign-in policy');
                const policy = await config.json();
                const required = policy.discord_required || policy.guest_approval_required;
                setDiscordRequired(required);
                const url = new URL(window.location.href);
                const tokenFromUrl = url.searchParams.get('session_token');
                const hasGuest = tokenFromUrl ? localStorage.getItem('guest_id') : pinnedGuestIdentity(url.search) || localStorage.getItem('guest_id');
                setCanAttemptGuestLogin(!required || (!!policy.approved_guest_login && !!hasGuest));
                // 1) Read token from URL (?session_token=...) if present
                let token: string | null = null;

                if (tokenFromUrl) {
                    token = tokenFromUrl;
                    // Persist and clean URL
                    localStorage.setItem('session_token', token);
                    url.searchParams.delete('session_token');
                    window.history.replaceState({}, '', url.toString());
                } else {
                    if (!required || policy.approved_guest_login) pinGuestIdentity(url.search, localStorage);

                    token = localStorage.getItem('session_token');
                }

                // If no token available, we're unauthenticated
                if (!token) {
                    setIsAuthenticated(false);
                    setIsUnauthorized(false);
                    return;
                }

                // 3) Validate token with /api/me using Bearer auth
                const res = await fetch('/api/me', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (res.ok) {
                    const data = await res.json();
                    setAccessToken(token);
                    setClientId(data.client_id);
                    setRole(data.role);
                    setIsAuthenticated(true);
                    setIsUnauthorized(false);
                } else if (res.status === 401 || res.status === 403) {
                    setIsAuthenticated(false);
                    setAccessToken(null);
                    localStorage.removeItem('session_token');
                    setIsUnauthorized(false);
                } else {
                    console.error('Unexpected /api/me status', res.status);
                }
            } catch (e) {
                setAuthError(e instanceof Error ? e.message : 'Sign-in failed.');
                setIsUnauthorized(true);
                console.error('Error calling /api/me', e);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Helper function to make authenticated API calls
    const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
        const token = accessToken || localStorage.getItem('session_token');
        if (!token) {
            setIsUnauthorized(true);
            setIsAuthenticated(false);
            throw new Error('Missing session token');
        }

        const response = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...(options.headers || {}),
            },
        });
        if (response.status === 401) {
            setIsUnauthorized(true);
            setIsAuthenticated(false);
        }
        return response;
    };

    const logout = async () => {
        try {
            // Attempt server-side logout if supported (optional)
            const token = accessToken || localStorage.getItem('session_token');
            if (token) {
                await fetch('/api/logout', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });
            }
        } catch (e) {
            console.error('logout error', e);
        }
        localStorage.removeItem('admin_active_view');
        localStorage.removeItem('session_token');
        setClientId(null);
        setRole(null);
        setIsUnauthorized(false);
        setIsAuthenticated(false);
        setAccessToken(null);
        window.location.reload();
    };

    const startDiscordLogin = async () => {
        try {
            const redirect = encodeURIComponent(window.location.href.split('?')[0]);
            const res = await fetch(`/api/login_discord?redirect=${redirect}`, { credentials: 'include' });
            if (res.ok) {
                const url = await res.text();
                if (url.startsWith('https://discord.com/')) {
                    window.location.href = url;
                } else {
                    console.error('Unexpected login_discord response', url);
                }
            } else {
                console.error('login_discord failed', res.status);
            }
        } catch (e) {
            console.error('Discord login initiation error', e);
        }
    };

    const doGuestLogin = async () => {
        try {
            if (!canAttemptGuestLogin) return;
            setAuthError(null);
            const pinned = pinnedGuestIdentity(window.location.search);
            let guestIdStr = pinned ?? localStorage.getItem('guest_id');
            if (guestIdStr === null) {
                guestIdStr = randomGuestIdentity();
            }
            localStorage.setItem('guest_id', guestIdStr);
            const res = await fetch('/api/login_guest', {
                method: "POST",
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ guest_id: guestIdStr })
            });
            if (!res.ok) {
                const error = await res.json().catch(() => null);
                throw new Error(error?.message || 'Guest sign-in was refused');
            }
            let tokenResponse = await res.json();

            localStorage.setItem('session_token', tokenResponse.session_token);
            setAccessToken(tokenResponse.session_token);
            setIsAuthenticated(true);
            setIsUnauthorized(false);
        } catch (e) {
            setAuthError(e instanceof Error ? e.message : 'Guest sign-in failed.');
            setIsUnauthorized(true);
            console.error('Guest login error', e);
        }
    };

    const value: AuthContextType = {
        discordRequired,
        canAttemptGuestLogin,
        authError,
        isAuthenticated,
        isLoading,
        isUnauthorized,
        role,
        clientId,
        accessToken,
        authenticatedFetch,
        startDiscordLogin,
        doGuestLogin,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
